import React, { useEffect, useState } from "react";
import useTripStore from "@/stores/tripBuilder/useTrip";
import { ItineraryItem } from "@/types/trip";
import { cn } from "@/lib/utils";
import logger from "@/utils/logger";
type CategoryKey = "transport" | "accommodation" | "activities";

interface CategoryConfig {
  key: CategoryKey;
  icon: string;
  label: string;
}

type BaseBookingStats = {
  completed: boolean;
  isPending: boolean;
};

type ActivityBookingStats = {
  completed: number;
  total: number;
};

type BookingStats =
  | BaseBookingStats
  | (BaseBookingStats & { count: ActivityBookingStats });

const CATEGORIES: CategoryConfig[] = [
  { key: "transport", icon: "fa-plane", label: "Transport" },
  { key: "accommodation", icon: "fa-bed", label: "Stay" },
  { key: "activities", icon: "fa-ticket", label: "Activities" },
];

const StatusIndicator: React.FC<{ stats: BookingStats }> = ({ stats }) => {
  if ("count" in stats && stats.count) {
    return stats.count.total > 0 ? (
      <div className="absolute -bottom-1 -right-2 bg-white text-[8px] sm:text-[10px] 3xl:text-[9px] rounded-full px-1 py-1 text-neutral-600 font-medium translate-x-1.5">
        {stats.count.completed}/{stats.count.total}
      </div>
    ) : null;
  }

  return (
    <div className="absolute -bottom-1 -right-1 w-3 h-3 sm:w-4 sm:h-4 3xl:w-4 3xl:h-4 rounded-full bg-white flex items-center justify-center">
      {stats.completed ? (
        <i className="fa-solid fa-check text-[8px] sm:text-[10px] 3xl:text-[9px] text-green-500" />
      ) : stats.isPending ? (
        <i className="fa-solid fa-minus text-[8px] sm:text-[10px] 3xl:text-[9px] text-orange-500" />
      ) : (
        <i className="fa-solid fa-xmark text-[8px] sm:text-[10px] 3xl:text-[9px] text-red-500" />
      )}
    </div>
  );
};

const CategoryCard: React.FC<{
  config: CategoryConfig;
  stats: BookingStats;
  variant: "compact" | "expanded";
}> = ({ config, stats, variant }) => {
  const isComplete =
    "count" in stats
      ? stats.count.completed === stats.count.total
      : stats.completed;
  const isPending = !isComplete && stats.isPending;

  const isCompact = variant === "compact";

  const baseClasses = cn(
    "transition-all duration-200 flex-1",
    {
      "flex flex-col items-center p-2 justify-between 3xl:p-1 rounded-lg":
        isCompact,
      "flex flex-row items-center px-1 py-2 2xl:py-4 2xl:px-2.5 rounded-lg justify-between h-full":
        !isCompact,
    },
    isComplete
      ? "bg-indigo-50/80"
      : isPending
      ? "bg-orange-50/80"
      : "bg-neutral-50/80"
  );

  const iconClasses = cn(
    "relative flex items-center justify-center",
    {
      "w-6 h-6 3xl:w-6 3xl:h-6 rounded-full px-6 items-center justify-center mx-auto":
        isCompact,
      "w-8 h-8 2xl:w-10 2xl:h-10 rounded-lg px-5 py-2": !isCompact,
    },
    isComplete
      ? "bg-indigo-100 text-indigo-600"
      : isPending
      ? "bg-orange-100 text-orange-500"
      : "bg-neutral-100 text-neutral-400"
  );

  const getStatusText = () => {
    if ("count" in stats) {
      if (stats.count.total === 0) return "Not booked";
      if (stats.count.completed === stats.count.total) return "Booked";
      return "In progress";
    }
    return isComplete ? "Booked" : isPending ? "Pending" : "Not booked";
  };

  return (
    <div className={baseClasses}>
      {!isCompact ? (
        <>
          <div className="flex flex-row items-center space-x-2">
            <div className={iconClasses}>
              <i
                className={cn("fa-solid", config.icon, {
                  "text-xs sm:text-base 3xl:text-xs": isCompact,
                  "text-sm 2xl:text-base": !isCompact,
                })}
              />
              <StatusIndicator stats={stats} />
            </div>
            <span className="text-xs 2xl:text-sm font-medium text-neutral-700">
              {config.label}
            </span>
          </div>
          <div
            className={cn(
              "inline-flex items-center justify-center px-2 py-1.5 2xl:px-3 2xl:py-2 rounded-full text-[10px] 2xl:text-sm font-medium",
              isComplete
                ? "bg-green-500/10 text-green-600"
                : isPending
                ? "bg-yellow-500/10 text-yellow-600"
                : "bg-gray-500/10 text-gray-600"
            )}
          >
            {getStatusText()}
          </div>
        </>
      ) : (
        <>
          <div className={iconClasses}>
            <i
              className={cn(
                "fa-solid",
                config.icon,
                "text-xs sm:text-base 3xl:text-sm"
              )}
            />
            <StatusIndicator stats={stats} />
          </div>
          <span className="text-[10px] sm:text-xs 3xl:text-[10px] mt-1 3xl:mt-0.5 font-medium text-neutral-600 min-h-[600px]:block hidden">
            {"count" in stats
              ? `${stats.count.completed}/${stats.count.total} Activities`
              : config.label}
          </span>
        </>
      )}
    </div>
  );
};

interface BookingProgressProps {
  locationId: string;
  variant?: "compact" | "expanded";
}

const BookingProgressOrigin: React.FC<BookingProgressProps> = ({
  locationId,
  variant,
}) => {
  const { tripData } = useTripStore();
  const selectedLocation = tripData?.locations.find(
    (location) => location.id === locationId
  );
  const [locationStats, setLocationStats] = useState<
    Record<CategoryKey, BookingStats>
  >({
    transport: { completed: false, isPending: false },
    accommodation: { completed: false, isPending: false },
    activities: {
      completed: false,
      isPending: false,
      count: { completed: 0, total: 0 },
    },
  });
  const [progress, setProgress] = useState<number>(0);

  useEffect(() => {
    if (!selectedLocation || !tripData) {
      setLocationStats({
        transport: { completed: false, isPending: false },
        accommodation: { completed: false, isPending: false },
        activities: {
          completed: false,
          isPending: false,
          count: { completed: 0, total: 0 },
        },
      });
      return;
    }

    logger.log("Calculating location stats", selectedLocation);

    // Find transport for this location
    const locationTransport = tripData.transport?.find(
      (transport) => transport.toLocationId === selectedLocation.id
    );

    // Get all Viator activities across all days
    const viatorActivities = selectedLocation.itinerary_items.reduce(
      (acc, day) =>
        acc.concat(
          day.items.filter(
            (item) => !("bookingInfo" in item) && item.type === "viator"
          )
        ),
      [] as ItineraryItem[]
    );

    // Get accommodation booking status
    const accommodationMain = selectedLocation.accommodation?.main;
    const accommodationStatus = accommodationMain?.bookingStatus;

    // Calculate stats for each category
    const stats: Record<CategoryKey, BookingStats> = {
      transport: {
        completed: locationTransport?.bookingStatus === "booked",
        isPending: locationTransport ? true : false,
      },
      accommodation: {
        completed: accommodationStatus === "booked",
        isPending:
          accommodationStatus === "pending" ||
          (accommodationMain !== null && accommodationStatus !== "booked") ||
          false,
      },
      activities: {
        completed: false,
        isPending: false,
        count: {
          completed: viatorActivities.filter(
            (activity) => activity.bookingStatus === "booked"
          ).length,
          total: viatorActivities.length,
        },
      },
    };

    if (process.env.NODE_ENV === "development") {
      logger.log("Location Stats Update:", {
        locationId: selectedLocation.id,
        accommodationStatus,
        transportStatus: locationTransport?.bookingStatus,
        activities: viatorActivities.map((activity) => ({
          id: activity.id,
          status: activity.bookingStatus,
        })),
        stats,
      });
    }

    const completedBookings = CATEGORIES.filter(({ key }) => {
      const categoryStats = stats[key];
      if ("count" in categoryStats) {
        return categoryStats.count.total > 0
          ? categoryStats.count.completed === categoryStats.count.total
          : false;
      }
      return categoryStats.completed;
    }).length;

    setProgress((completedBookings / CATEGORIES.length) * 100);
    setLocationStats(stats);
  }, [selectedLocation, tripData]);

  return (
    <div
      className="w-full h-full bg-white rounded-lg flex flex-col px-2 pt-2 2xl:px-4 2xl:py-3"
      data-tutorial="overview-booking-progress"
    >
      <div className="flex flex-col w-full h-full">
        <div className="h-full">
          <div className="h-1 w-full bg-neutral-100 rounded-full mb-2 mt-1 2xl:mt-2">
            <div
              className="h-full 2xl:h-1.5 bg-indigo-600 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>

          <div className="flex flex-col space-y-1 2xl:space-y-3 mt-2 2xl:mt-5 h-[calc(100%-1.5rem)] justify-between">
            {CATEGORIES.map((category) => (
              <CategoryCard
                key={category.key}
                config={category}
                stats={locationStats[category.key]}
                variant="expanded"
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingProgressOrigin;
